import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Building2, PhoneCall, Trash2, Upload } from 'lucide-react';
import { Card, CardBody, CardHeader } from '@/components/card';
import { Button } from '@/components/button';
import { Input } from '@/components/input';
import { Select } from '@/components/select';
import { Textarea } from '@/components/textarea';
import { useToast } from '@/components/toast';
import { useFormError } from '@/lib/form-error';
import {
  useBusinessSettings,
  useDeleteVoiceClip,
  useUpdateBusinessSettings,
  useUploadVoiceClip,
  type VoiceClipSlot,
} from '@/lib/business-settings';
import { US_STATES } from '@/lib/us-states';
import type { BusinessSettingData } from '@/types/api';

export function BusinessTab() {
  const me = useBusinessSettings();
  const update = useUpdateBusinessSettings();
  const toast = useToast();
  const { error, wrap } = useFormError();

  const [form, setForm] = useState<Partial<BusinessSettingData>>({});
  const [hourlyRate, setHourlyRate] = useState('');

  useEffect(() => {
    if (me.data) {
      setForm(me.data);
      setHourlyRate(
        me.data.default_hourly_rate_cents != null
          ? (me.data.default_hourly_rate_cents / 100).toString()
          : '',
      );
    }
  }, [me.data]);

  function setField<K extends keyof BusinessSettingData>(key: K, value: BusinessSettingData[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const patch: Partial<BusinessSettingData> = {
      ...form,
      default_hourly_rate_cents:
        hourlyRate.trim() === '' ? null : Math.round(parseFloat(hourlyRate) * 100),
    };
    const ok = await wrap(() => update.mutateAsync(patch));
    if (ok) toast.success('Business info saved');
  }

  if (!me.data) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader>
          <h3 className="flex items-center gap-2 text-sm font-semibold">
            <Building2 className="size-4 text-muted-foreground" /> Business info
          </h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Used as the header on proposals, invoices, and outbound emails.
          </p>
        </CardHeader>
        <CardBody>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Legal name">
                <Input
                  value={form.legal_name ?? ''}
                  onChange={(e) => setField('legal_name', e.target.value)}
                  placeholder="Mehrutherm LLC"
                />
              </Field>
              <Field label="Tax ID / EIN">
                <Input
                  value={form.tax_id ?? ''}
                  onChange={(e) => setField('tax_id', e.target.value)}
                  placeholder="00-0000000"
                />
              </Field>
              <Field label="Email">
                <Input
                  type="email"
                  value={form.email ?? ''}
                  onChange={(e) => setField('email', e.target.value)}
                  placeholder="hi@mehrutherm.com"
                />
              </Field>
              <Field label="Phone">
                <Input
                  value={form.phone ?? ''}
                  onChange={(e) => setField('phone', e.target.value)}
                  placeholder="(425) 555-0188"
                />
              </Field>
              <Field label="Website" className="sm:col-span-2">
                <Input
                  value={form.website ?? ''}
                  onChange={(e) => setField('website', e.target.value)}
                  placeholder="https://mehrutherm.com"
                />
              </Field>
            </div>

            <div className="border-t border-border/60 pt-4">
              <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Address
              </h4>
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="Street" className="sm:col-span-2">
                  <Input
                    value={form.address_line1 ?? ''}
                    onChange={(e) => setField('address_line1', e.target.value)}
                  />
                </Field>
                <Field label="Suite / unit (optional)" className="sm:col-span-2">
                  <Input
                    value={form.address_line2 ?? ''}
                    onChange={(e) => setField('address_line2', e.target.value)}
                  />
                </Field>
                <Field label="City">
                  <Input
                    value={form.city ?? ''}
                    onChange={(e) => setField('city', e.target.value)}
                  />
                </Field>
                <Field label="State">
                  <Select
                    value={form.state ?? ''}
                    onChange={(e) => setField('state', e.target.value || null)}
                  >
                    <option value="">—</option>
                    {US_STATES.map((s) => (
                      <option key={s.code} value={s.code}>
                        {s.code} · {s.name}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="ZIP">
                  <Input
                    value={form.postal_code ?? ''}
                    onChange={(e) => setField('postal_code', e.target.value)}
                  />
                </Field>
                <Field label="Country">
                  <Input
                    value={form.country ?? 'US'}
                    onChange={(e) => setField('country', e.target.value)}
                    maxLength={2}
                  />
                </Field>
              </div>
            </div>

            <div className="border-t border-border/60 pt-4">
              <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Defaults
              </h4>
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="Hourly rate ($)">
                  <Input
                    inputMode="decimal"
                    placeholder="0.00"
                    value={hourlyRate}
                    onChange={(e) => setHourlyRate(e.target.value)}
                  />
                </Field>
                <Field label="Currency">
                  <Input
                    value={form.default_currency ?? 'USD'}
                    onChange={(e) => setField('default_currency', e.target.value.toUpperCase())}
                    maxLength={3}
                  />
                </Field>
                <Field label="Invoice notes" className="sm:col-span-2">
                  <Textarea
                    rows={4}
                    value={form.default_invoice_notes ?? ''}
                    onChange={(e) => setField('default_invoice_notes', e.target.value || null)}
                    placeholder="Payment terms, thank-you note, or anything you want on every new invoice by default."
                  />
                  <span className="mt-1 block text-xs text-muted-foreground">
                    Prefilled into the notes on each new invoice. You can still edit or clear it per
                    invoice.
                  </span>
                </Field>
              </div>
            </div>

            <div className="border-t border-border/60 pt-4">
              <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Email
              </h4>
              <div className="mt-3">
                <Field label="Email signature">
                  <Textarea
                    rows={4}
                    value={form.email_signature ?? ''}
                    onChange={(e) => setField('email_signature', e.target.value || null)}
                    placeholder={'Thanks,\nHimanshu Mehru\nMehrutherm LLC'}
                  />
                  <span className="mt-1 block text-xs text-muted-foreground">
                    The closing shown at the bottom of every outbound email (invoices, receipts,
                    invitations). Include your own sign-off line. Left blank, emails use "Thanks, the{' '}
                    {form.legal_name || 'company'} team".
                  </span>
                </Field>
              </div>
            </div>

            {error ? <p className="text-sm text-destructive">{error}</p> : null}
            <div className="flex justify-end">
              <Button type="submit" disabled={update.isPending}>
                {update.isPending ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>

      <VoiceClipsCard settings={me.data} />
    </div>
  );
}

/**
 * The two recordings the phone line plays. Both are optional: with nothing
 * uploaded the line reads the same text out in a synthetic voice, so removing a
 * clip is always safe.
 */
function VoiceClipsCard({ settings }: { settings: BusinessSettingData }) {
  return (
    <Card>
      <CardHeader>
        <h3 className="flex items-center gap-2 text-sm font-semibold">
          <PhoneCall className="size-4 text-muted-foreground" /> Phone greetings
        </h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Record these in your own voice, in MP3 or WAV. Mono is plenty: the phone
          network throws the rest away. Leave one empty to have it read aloud instead.
        </p>
      </CardHeader>
      <CardBody className="space-y-4">
        <VoiceClip
          slot="screening_prompt"
          label="Screening prompt"
          hint="Played to a caller we don't recognise. It has to ask them to press 1."
          url={settings.screening_prompt_url}
        />
        <VoiceClip
          slot="voicemail_greeting"
          label="Voicemail greeting"
          hint="Played when nobody picks up, just before the beep."
          url={settings.voicemail_greeting_url}
        />
      </CardBody>
    </Card>
  );
}

function VoiceClip({
  slot,
  label,
  hint,
  url,
}: {
  slot: VoiceClipSlot;
  label: string;
  hint: string;
  url: string | null;
}) {
  const upload = useUploadVoiceClip();
  const remove = useDeleteVoiceClip();
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      await upload.mutateAsync({ slot, file });
      toast.success(`${label} uploaded`);
    } catch {
      toast.error('That file could not be used. MP3 or WAV, up to 5 MB.');
    }
    if (input.current) input.current.value = '';
  }

  return (
    <div className="rounded-lg border border-border/70 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-medium">{label}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <input
            ref={input}
            type="file"
            accept="audio/mpeg,audio/wav,.mp3,.wav"
            className="hidden"
            onChange={onPick}
          />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={upload.isPending}
            onClick={() => input.current?.click()}
          >
            <Upload className="size-4" />
            {url ? 'Replace' : 'Upload'}
          </Button>
          {url ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={remove.isPending}
              onClick={async () => {
                await remove.mutateAsync(slot);
                toast.success(`${label} removed`);
              }}
            >
              <Trash2 className="size-4" />
            </Button>
          ) : null}
        </div>
      </div>

      {url ? (
        <audio controls src={url} className="mt-3 w-full" preload="none">
          <track kind="captions" />
        </audio>
      ) : null}
    </div>
  );
}

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label className={`block space-y-1.5 ${className ?? ''}`}>
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
